<template>
	<v-card variant="outlined">
		<v-card-text>
			<!-- Display Artist/ Title-->
			<div class="d-flex flex-row align-center">
				<div class="d-flex flex-column w-50 ml-4 mr-2">
					<label class="text-caption font-weight-bold">
						Display Artist
					</label>
					<v-text-field v-model="formData.DisplayArtist"
						variant="outlined"
						density="compact"
						hide-details
						:disabled="!editAssetFeatureEnabled"
					/>
				</div>
				<div class="d-flex flex-column w-50 ml-4 mr-2">
					<label class="text-caption font-weight-bold">
						Display Title
					</label>
					<v-text-field v-model="formData.DisplayTitle"
						class="mr-4"
						variant="outlined"
						density="compact"
						hide-details
						:disabled="!editAssetFeatureEnabled"
					/>
				</div>
			</div>

			<!-- End Date Time-->
			<div class="d-flex flex-row align-center my-4 w-50">
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold"> End Date Time</label>
					<div class="d-flex">
						<v-menu :close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" class="flex-grow-1" variant="outlined"
									:disabled="!editEndDateFeatureEnabled || !editAssetFeatureEnabled">
									<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
									<span class="ml-3 text-secondary">
									{{ formattedEndDateTime }}
								</span>
								</v-btn>
							</template>
							<template v-slot:default="{ isActive }">
								<v-date-picker v-model="formData.EndDateTime"
									color="secondary"
									hide-header
									show-adjacent-months
									@update:model-value="isActive.value = false">
								</v-date-picker>
							</template>
						</v-menu>
						<v-btn icon class="ml-2"
							density="comfortable" rounded="lg" variant="outlined"
							:disabled="!editEndDateFeatureEnabled || !editAssetFeatureEnabled"
							@click.stop="formData.EndDateTime = null"
						>
							<v-icon icon="mdi-close" size="24"/>
						</v-btn>
					</div>
				</div>
			</div>

			<!-- Comment/ Created Date -->
			<div class="d-flex flex-row align-center my-4">
				<div class="d-flex flex-column w-50 ml-4 mr-2">
					<label class="text-caption font-weight-bold">
						Comment
					</label>
					<v-text-field v-model="formData.Comment"
						variant="outlined"
						density="compact"
						hide-details
						:disabled="!editAssetFeatureEnabled"
					/>
				</div>
				<div class="d-flex flex-column w-50 ml-4 mr-2">
					<label class="text-caption font-weight-bold"> Created </label>
					<div class="d-flex justify-space-between">
						<v-btn variant="outlined" class="flex-grow-1">
							<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
							<span class="ml-3 text-secondary">{{ formattedCreatedDateTime }}</span>
						</v-btn>
					</div>
				</div>
			</div>

			<!-- Bed Type/ Normalize Audio/ Created By -->
			<div class="d-flex flex-row align-center my-4">
				<div class="d-flex flex-row w-25 ml-4">
					<div class="d-inline-flex align-center">
						<v-select v-model="formData.BedType"
							:items="BedTypes.getArrayValidItems()"
							label="Bed Type"
							density="compact"
							variant="outlined"
							hide-details
							:disabled="!editAssetFeatureEnabled"/>
						<v-tooltip text="Start next event at early segue or start of audio" location="bottom">
							<template v-slot:activator="{ props }">
								<v-icon v-bind="props" class="ml-2" icon="mdi-help-circle-outline"/>
							</template>
						</v-tooltip>
					</div>

				</div>
				<div class="d-flex flex-row w-25">
					<div class="d-inline-flex align-center">
						<v-checkbox v-model="formData.BypassAudioNormalization"
							label="Normalize Audio"
							density="compact"
							hide-details
							:disabled="!editAssetFeatureEnabled"/>
						<v-tooltip text="Increases and/ or decreases the audio playback level to a 'normal' value (set in Station Config) to avoid
									large variations from quiet-to-loud or loud-to-quiet segues and playback audio at a more uniform level.
									Un-checking will playback the audio without any audio volume adjustments."
							location="bottom">
							<template v-slot:activator="{ props }">
								<v-icon v-bind="props" class="ml-2" icon="mdi-help-circle-outline"/>
							</template>
						</v-tooltip>

					</div>
				</div>
				<div class="d-flex flex-column w-50 ml-4 mr-2">
					<label class="text-caption font-weight-bold">
						Created By
					</label>
					<v-text-field v-model="formData.CreatedBy"
						variant="outlined"
						density="compact"
						hide-details
						:disabled="!editAssetFeatureEnabled"
					/>
				</div>
			</div>

			<!-- Kill/ Modified Date -->
			<div class="d-flex flex-row align-center my-4">
				<div class="d-flex flex-column w-50 ml-4 mr-2">
					<label class="text-caption font-weight-bold"> Kill Date Time</label>
					<div class="d-flex justify-space-between">
						<v-menu :close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" class="flex-grow-1" variant="outlined"
									:disabled="!editKillDateFeatureEnabled || !editAssetFeatureEnabled">
									<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
									<span class="ml-3 text-secondary">
									{{ formattedKillDateTime }}
								</span>
								</v-btn>
							</template>
							<template v-slot:default="{ isActive }">
								<v-date-picker
									v-model="formData.KillDateTime"
									color="secondary"
									hide-header
									show-adjacent-months
									@update:model-value="isActive.value = false">
								</v-date-picker>
							</template>
						</v-menu>
						<v-btn icon class="ml-2"
							density="comfortable" rounded="lg" variant="outlined"
							:disabled="!editKillDateFeatureEnabled || !editAssetFeatureEnabled"
							@click.stop="formData.KillDateTime = null"
						>
							<v-icon icon="mdi-close" size="24"/>
						</v-btn>
					</div>
				</div>
				<div class="d-flex flex-column w-50 ml-4 mr-2"><label
					class="text-caption font-weight-bold">Modified </label>
					<div class="d-flex justify-space-between">
						<v-btn variant="outlined" class="flex-grow-1">
							<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
							<span class="ml-3 text-secondary">{{ formattedModifiedDateTime }}</span>
						</v-btn>
					</div>
				</div>
			</div>

			<!-- Release Date -->
			<div class="d-flex flex-row align-center my-4 w-50">
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold"> Release Date </label>
					<div class="d-flex justify-space-between">
						<v-menu :close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" class="flex-grow-1" variant="outlined"
									:disabled="!editReleaseDateFeatureEnabled || !editAssetFeatureEnabled">
									<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
									<span class="ml-3 text-secondary">
									{{ formattedReleasedDateTime }}
								</span>
								</v-btn>
							</template>
							<template v-slot:default="{ isActive }">
								<v-date-picker
									v-model="formData.ReleaseDateTime"
									color="secondary"
									hide-header
									show-adjacent-months
									@update:model-value="isActive.value = false">
								</v-date-picker>
							</template>
						</v-menu>
						<v-btn icon class="ml-2"
							density="comfortable" rounded="lg" variant="outlined"
							:disabled="!editReleaseDateFeatureEnabled || !editAssetFeatureEnabled"
							@click.stop="formData.ReleaseDateTime = null"
						>
							<v-icon icon="mdi-close" size="24"/>
						</v-btn>
					</div>
				</div>
			</div>

			<!-- Audio File -->
			<div class="d-flex flex-column ml-4 my-4 w-100">
				<label class="text-caption font-weight-bold"> Audio File </label>
				<v-file-input v-model="formData.ImportFile"
					accept="audio/*"
					chips
					:label="!formData.ImportFile ? formData.OriginalFileName : ''"
					variant="outlined"
					density="compact"
					prepend-icon=""
					hide-details
					:disabled="mode === Modes.Edit && (!editImportFileFeatureEnabled || !editAssetFeatureEnabled)">
					<template v-slot:prepend-inner>
						<v-icon icon="mdi-close"
							size="24"
							@click.stop="formData.ImportFile = null"
						/>
						<v-btn color="secondary"
							prepend-icon="mdi-music"
							variant="flat"
							class="mx-2"
						>
							Select File
						</v-btn>
					</template>
				</v-file-input>
			</div>

			<!-- Tags -->
			<div class="d-flex flex-column ml-4 my-4 w-100">
				<label class="text-caption font-weight-bold"> Tags </label>
				<v-combobox v-model="formData.Tags"
					variant="outlined"
					density="compact"
					multiple
					chips
					closable-chips
					hide-details
					:delimiters="[',']"
					:disabled="!editTagsFeatureEnabled || !editAssetFeatureEnabled"
				/>
			</div>

			<!-- Third Party IDs -->
			<div class="d-flex flex-column ml-4 my-4">
				<label class="text-caption font-weight-bold"> Third Party IDs </label>
				<div class="d-flex flex-row my-1 w-100">
					<div class="text-caption font-weight-bold w-50 ml-4 mr-2">
						Key
					</div>
					<div class="text-caption font-weight-bold w-50 ml-4 mr-2">
						Value
					</div>
				</div>
				<div v-for="(_, key) in formData.ThirdPartyIDs" :key="key" class="d-flex flex-row w-100"
					:class="{ 'text-disabled': !editThirdPartyIDsFeatureEnabled || !editAssetFeatureEnabled}">
					<v-text-field :model-value="key"
						class="w-50 ml-4 mr-2 my-1"
						variant="outlined"
						density="compact"
						hide-details
					/>
					<v-text-field v-model="formData.ThirdPartyIDs[key]"
						class="w-50 ml-4 mr-2 my-1"
						variant="outlined"
						density="compact"
						hide-details
					/>
				</div>
			</div>

			<!-- Custom Fields-->
			<div class="d-flex flex-column ml-4 my-4">
				<label class="text-caption font-weight-bold"> Custom Fields</label>
				<div class="d-flex flex-row my-1 w-100">
					<div class="text-caption font-weight-bold w-50 ml-4 mr-2">
						Key
					</div>
					<div class="text-caption font-weight-bold w-50 ml-4 mr-2">
						Value
					</div>
				</div>
				<div v-for="(value, key) in formData.CustomFields" :key="key" class="d-flex flex-row w-100"
					:class="{ 'text-disabled': !editCustomFieldsFeatureEnabled || !editAssetFeatureEnabled}">
					<v-text-field :model-value="key"
						class="w-50 ml-4 mr-2 my-1"
						variant="outlined"
						density="compact"
						hide-details
					/>
					<v-text-field v-model="formData.CustomFields[key]"
						class="w-50 ml-4 mr-2 my-1"
						variant="outlined"
						density="compact"
						hide-details
					/>
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
/* Enums */
import Modes from "@/enums/AssetManagementModeTypes";
import BedTypes from "@/enums/BedTypes";

/* Libraries */
import moment from "moment-timezone";
import _ from "lodash";

export default {
	name: "AssetManagementGlobalExtraTab",

	emits: ["update:asset"],

	data() {
		return {
			formData: {}
		}
	},

	computed: {
		BedTypes() {
			return BedTypes
		},

		mode() {
			return this.$store.getters.getAssetManagementMode();
		},

		Modes() {
			return Modes;
		},

		event() {
			return this.$store.getters.getAssetManagementEvent();
		},

		asset() {
			return this.$store.getters.getAssetManagementAsset();
		},

		formattedEndDateTime() {
			return this.formData.EndDateTime ?
			       moment(this.formData.EndDateTime).format('MMM D, YYYY') :
			       "End Date"
		},

		formattedCreatedDateTime() {
			return this.formData.CreatedDateTime ?
			       moment(this.formData.CreatedDateTime).format('MMM D, YYYY') :
			       "Created Date";
		},

		formattedKillDateTime() {
			return this.formData.KillDateTime ?
			       moment(this.formData.KillDateTime).format('MMM D, YYYY') :
			       "Kill Date"
		},

		formattedModifiedDateTime() {
			return this.formData.ModifiedDateTime ?
			       moment(this.formData.ModifiedDateTime).format('MMM D, YYYY') :
			       "Modified Date"
		},

		formattedReleasedDateTime() {
			return this.formData.ReleaseDateTime ?
			       moment(this.formData.ReleaseDateTime).format('MMM D, YYYY') :
			       "Release Date"
		},

		editEndDateFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_enddatetime",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		editKillDateFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_killdatetime",
				asset_type_id: this.asset?.AssetTypeID
			});
		},

		editReleaseDateFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_releasedatetime",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		editTagsFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_tags",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		editImportFileFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.formData.ImportFile ?
				   this.$store.getters.isFeatureEnabledByAssetTypeID({
					   object_id: this.$store.getters.assetManagementStationID(),
					   reference: "zettacloud_asset_update_audio",
					   asset_type_id: this.asset.AssetTypeID
				   }) :
				   this.$store.getters.isFeatureEnabledByAssetTypeID({
					   object_id: this.$store.getters.assetManagementStationID(),
					   asset_type_id: this.asset.AssetTypeID,
					   reference: "zettacloud_asset_import_musicsource"
				   });
		},

		editThirdPartyIDsFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_third_party_ids",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		editCustomFieldsFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_custom_fields",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		editAssetFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit",
				asset_type_id: this.asset.AssetTypeID
			});
		}
	},

	methods: {
		initFormData() {
			if (this.asset) {
				this.formData = JSON.parse(JSON.stringify(
					_.pick(this.asset, [
						"DisplayArtist",
						"DisplayTitle",
						"EndDateTime",
						"Comment",
						"CreatedDateTime",
						"CreatedBy",
						"KillDateTime",
						"ModifiedDateTime",
						"ReleaseDateTime",
						"BedType",
						"Tags",
						"ThirdPartyIDs",
						"CustomFields",
						"OriginalFileName"
					])
				));
				if (!this.formData.BedType) {
					this.formData.BedType = BedTypes.Invalid;
				}
				this.formData.EndDateTime = this.formData.EndDateTime ? moment(this.formData.EndDateTime) : null;
				this.formData.CreatedDateTime = this.formData.CreatedDateTime ? moment(this.formData.CreatedDateTime) :
				                                null;
				this.formData.KillDateTime = this.formData.KillDateTime ? moment(this.formData.KillDateTime) : null;
				this.formData.ModifiedDateTime = this.formData.ModifiedDateTime ?
				                                 moment(this.formData.ModifiedDateTime) :
				                                 null;
				this.formData.ReleaseDateTime = this.formData.ReleaseDateTime ? moment(this.formData.ReleaseDateTime) :
				                                null;
			}
			else {
				const now = moment();
				this.formData.KillDateTime = now.clone().add(5, "d");
			}
		},

		getData() {
			if (typeof this.formData.EndDateTime !== "string") {
				this.formData.EndDateTime = this.formData?.EndDateTime?.toISOString();
			}
			if (typeof this.formData.KillDateTime !== "string") {
				this.formData.KillDateTime = this.formData?.KillDateTime?.toISOString();
			}
			if (typeof this.formData.ReleaseDateTime !== "string") {
				this.formData.ReleaseDateTime = this.formData?.ReleaseDateTime?.toISOString();
			}
			if (this.formData.ImportFile) {
				this.formData.OriginalFileName = this.formData.ImportFile[0].name;
			}

			return this.asset ?
			       _.pickBy(this.formData, (value, key) => !_.isEqual(value, this.asset[key])) :
			       _.omitBy(
					   _.pick(this.formData, ["EndDateTime", "KillDateTime", "ReleaseDateTime", "ImportFile"]),
					   _.isNil
				   );
		}
	},

	watch: {
		asset() {
			this.initFormData();
		}
	},

	mounted() {
		this.initFormData();
	}
}
</script>

<style scoped lang="scss">

</style>